import {
  forwardRef,
  useId,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { Link } from "react-router";
import { twMerge } from "tailwind-merge";
import { ChevronRight, Loader2 } from "lucide-react";
import { joinDuration, splitMinutes } from "@shared/duration";

/** Join class names; later Tailwind utilities override earlier conflicting ones (e.g. Card's p-4 vs a caller's p-3). */
export function cx(...classes: (string | false | null | undefined)[]) {
  return twMerge(classes.filter(Boolean).join(" "));
}

type Variant = "primary" | "secondary" | "ghost" | "danger" | "coin" | "soft";
type Size = "sm" | "md" | "lg";

const variants: Record<Variant, string> = {
  primary: "bg-brand text-on-brand hover:bg-brand-strong active:scale-[0.98] shadow-sm",
  secondary: "bg-card text-ink ring-1 ring-line hover:bg-sunken active:scale-[0.98]",
  ghost: "text-ink hover:bg-sunken",
  danger: "bg-danger text-white hover:opacity-90 active:scale-[0.98]",
  coin: "bg-coin text-white hover:brightness-105 active:scale-[0.98] shadow-sm",
  soft: "bg-brand-soft text-brand-strong hover:brightness-95 active:scale-[0.98]",
};
const sizes: Record<Size, string> = {
  sm: "h-9 px-3 text-sm rounded-xl gap-1.5",
  md: "h-11 px-4 text-[15px] rounded-2xl gap-2",
  lg: "h-14 px-6 text-lg rounded-2xl gap-2",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  block?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", loading, block, className, children, disabled, type = "button", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cx(
        "inline-flex select-none items-center justify-center font-bold transition disabled:opacity-50",
        variants[variant],
        sizes[size],
        block && "w-full",
        className,
      )}
      {...rest}
    >
      {loading && <Loader2 className="size-4 animate-spin" />}
      {children}
    </button>
  );
});

export function IconButton({
  label,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cx("inline-flex size-10 items-center justify-center rounded-full text-ink transition hover:bg-sunken", className)}
      {...rest}
    >
      {children}
    </button>
  );
}

export function Card({ className, children, ...rest }: { className?: string; children: ReactNode } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cx("rounded-2xl bg-card p-4 ring-1 ring-line", className)} {...rest}>
      {children}
    </div>
  );
}

export function SectionTitle({ children, action, className }: { children: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cx("mb-2 mt-6 flex items-end justify-between gap-2 px-1", className)}>
      <h2 className="text-lg font-extrabold">{children}</h2>
      {action}
    </div>
  );
}

type BadgeTone = "brand" | "coin" | "danger" | "warn" | "locked" | "neutral" | "violet";
const badgeTones: Record<BadgeTone, string> = {
  brand: "bg-brand-soft text-brand-strong",
  coin: "bg-coin-soft text-coin-ink",
  danger: "bg-danger-soft text-danger",
  warn: "bg-warn-soft text-warn",
  locked: "bg-locked-soft text-locked",
  neutral: "bg-sunken text-muted",
  violet: "bg-violet-soft text-violet",
};

export function Badge({ tone = "neutral", children, className }: { tone?: BadgeTone; children: ReactNode; className?: string }) {
  return (
    <span className={cx("inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-bold", badgeTones[tone], className)}>
      {children}
    </span>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
  htmlFor,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  children: ReactNode;
  htmlFor?: string;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="block text-sm font-bold text-ink">
        {label}
      </label>
      {children}
      {error ? <p className="text-sm font-semibold text-danger">{error}</p> : hint ? <p className="text-xs text-muted">{hint}</p> : null}
    </div>
  );
}

const inputClass =
  "w-full rounded-2xl border-0 bg-sunken px-4 py-3 text-ink placeholder:text-faint ring-1 ring-line focus:bg-card focus:outline-none focus:ring-2 focus:ring-brand";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input(
  { className, ...rest },
  ref,
) {
  return <input ref={ref} className={cx(inputClass, className)} {...rest} />;
});

export function TextField({
  label,
  hint,
  error,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; hint?: ReactNode; error?: string | null }) {
  const id = useId();
  return (
    <Field label={label} hint={hint} error={error} htmlFor={id}>
      <Input id={id} {...rest} />
    </Field>
  );
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cx(inputClass, "min-h-[88px] resize-y", className)} {...rest} />;
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={cx(inputClass, "appearance-none bg-[length:16px] pr-10", className)} {...rest}>
      {children}
    </select>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <label className={cx("flex cursor-pointer items-start justify-between gap-4 py-1", disabled && "opacity-50")}>
      <span className="min-w-0">
        <span className="block font-bold">{label}</span>
        {description && <span className="mt-0.5 block text-sm text-muted">{description}</span>}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cx(
          "relative mt-0.5 h-7 w-12 shrink-0 rounded-full transition",
          checked ? "bg-brand" : "bg-line",
        )}
      >
        <span
          className={cx(
            "absolute top-0.5 size-6 rounded-full bg-white shadow transition-all",
            checked ? "left-[22px]" : "left-0.5",
          )}
        />
      </button>
    </label>
  );
}

export function Segmented<T extends string>({
  value,
  onChange,
  options,
  className,
}: {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: ReactNode }[];
  className?: string;
}) {
  return (
    <div className={cx("flex rounded-2xl bg-sunken p-1 ring-1 ring-line", className)} role="radiogroup">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={cx(
            "flex-1 rounded-xl px-3 py-2 text-sm font-bold transition",
            value === o.value ? "bg-card text-ink shadow-sm" : "text-muted hover:text-ink",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cx("size-6 animate-spin text-brand", className)} />;
}

export function PageLoader() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <Spinner className="size-8" />
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  children,
  action,
}: {
  icon: ReactNode;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center rounded-3xl border-2 border-dashed border-line px-6 py-10 text-center">
      <div className="mb-3 text-4xl">{icon}</div>
      <h3 className="text-lg font-extrabold">{title}</h3>
      {children && <p className="mt-1 max-w-sm text-sm text-muted">{children}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: { message: string } | null; onRetry?: () => void }) {
  return (
    <div className="rounded-3xl bg-danger-soft p-5 text-center text-danger">
      <p className="font-bold">{error?.message ?? "Something went wrong"}</p>
      {onRetry && (
        <Button variant="secondary" size="sm" className="mt-3" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

export function ListLink({
  to,
  icon,
  title,
  subtitle,
  trailing,
}: {
  to: string;
  icon: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <Link to={to} className="flex items-center gap-3 rounded-2xl px-3 py-3 transition hover:bg-sunken">
      <span className="flex size-10 shrink-0 items-center justify-center rounded-2xl bg-sunken text-xl">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block font-bold">{title}</span>
        {subtitle && <span className="block truncate text-sm text-muted">{subtitle}</span>}
      </span>
      {trailing}
      <ChevronRight className="size-5 text-faint" />
    </Link>
  );
}

export function Stat({ label, value, tone }: { label: ReactNode; value: ReactNode; tone?: "brand" | "coin" | "danger" }) {
  return (
    <div className="rounded-2xl bg-sunken px-3 py-2">
      <div className="text-xs font-bold text-muted">{label}</div>
      <div
        className={cx(
          "text-lg font-extrabold tabular-nums",
          tone === "brand" && "text-brand",
          tone === "coin" && "text-coin-ink",
          tone === "danger" && "text-danger",
        )}
      >
        {value}
      </div>
    </div>
  );
}

/**
 * A number field that shows exactly what the user types — including an empty field — while it
 * has focus, so they can clear it and type a new value. Each edit reports the parsed number
 * (empty counts as 0) for the caller to clamp; on blur the field shows the caller's value again.
 */
export function NumericInput({
  value,
  onChange,
  decimal = false,
  onBlur,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "inputMode"> & {
  value: number;
  onChange: (v: number) => void;
  /** Allow one decimal point (prices, rates). */
  decimal?: boolean;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <Input
      {...rest}
      inputMode={decimal ? "decimal" : "numeric"}
      value={draft ?? (Number.isFinite(value) ? String(value) : "")}
      onChange={(e) => {
        const text = cleanNumber(e.target.value, decimal);
        setDraft(text);
        const n = Number(text === "." ? "" : text);
        onChange(Number.isFinite(n) ? n : 0);
      }}
      onBlur={(e) => {
        setDraft(null);
        onBlur?.(e);
      }}
    />
  );
}

/** Digits (and one "." when decimal), without leading zeros: "05" → "5", "0.5" stays. */
function cleanNumber(text: string, decimal: boolean): string {
  let t = text.replace(decimal ? /[^0-9.]/g : /\D/g, "");
  const dot = t.indexOf(".");
  if (dot >= 0) t = t.slice(0, dot + 1) + t.slice(dot + 1).replace(/\./g, "");
  return t.replace(/^0+(?=\d)/, "");
}

export function NumberStepper({
  value,
  onChange,
  min = 0,
  max = 100000,
  step = 1,
  suffix,
  label,
}: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: ReactNode;
  /** Accessible name for the field. */
  label?: string;
}) {
  const clamp = (v: number) => Math.min(max, Math.max(min, v));
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        aria-label="Decrease"
        className="size-11 shrink-0 rounded-2xl bg-sunken text-xl font-extrabold ring-1 ring-line"
        onClick={() => onChange(clamp(value - step))}
      >
        −
      </button>
      <div className="relative flex-1">
        <NumericInput decimal aria-label={label} value={value} onChange={(n) => onChange(clamp(n))} className="text-center text-lg font-extrabold tabular-nums" />
        {suffix && <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm text-muted">{suffix}</span>}
      </div>
      <button
        type="button"
        aria-label="Increase"
        className="size-11 shrink-0 rounded-2xl bg-sunken text-xl font-extrabold ring-1 ring-line"
        onClick={() => onChange(clamp(value + step))}
      >
        +
      </button>
    </div>
  );
}

/**
 * Hours + minutes entry for a duration stored as total minutes (screen time, caps, sessions).
 * Minutes are limited to 0–59; the − / + buttons move the total by `step` minutes.
 */
export function DurationInput({
  value,
  onChange,
  min = 0,
  max = 7 * 24 * 60,
  step = 15,
  label,
}: {
  value: number;
  onChange: (minutes: number) => void;
  min?: number;
  max?: number;
  step?: number;
  /** Prefix for the fields' accessible names, e.g. "Per week" → "Per week hours". */
  label?: string;
}) {
  const clamp = (v: number) => Math.min(max, Math.max(min, Math.round(v)));
  const { hours, minutes } = splitMinutes(value);
  const name = (part: string) => (label ? `${label} ${part.toLowerCase()}` : part);
  const field = "text-center text-lg font-extrabold tabular-nums pr-12";
  const unit = "pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted";
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        aria-label="Decrease"
        className="size-11 shrink-0 rounded-2xl bg-sunken text-xl font-extrabold ring-1 ring-line"
        onClick={() => onChange(clamp(value - step))}
      >
        −
      </button>
      <div className="relative min-w-0 flex-1">
        <NumericInput aria-label={name("Hours")} value={hours} onChange={(h) => onChange(clamp(joinDuration(h, minutes)))} className={field} />
        <span className={unit}>hr</span>
      </div>
      <div className="relative min-w-0 flex-1">
        <NumericInput
          aria-label={name("Minutes")}
          maxLength={2}
          value={minutes}
          onChange={(m) => onChange(clamp(joinDuration(hours, m)))}
          className={field}
        />
        <span className={unit}>min</span>
      </div>
      <button
        type="button"
        aria-label="Increase"
        className="size-11 shrink-0 rounded-2xl bg-sunken text-xl font-extrabold ring-1 ring-line"
        onClick={() => onChange(clamp(value + step))}
      >
        +
      </button>
    </div>
  );
}
